import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Plus, Search, Filter, Download, FileText, FileSpreadsheet,
  AlertTriangle, Clock, Bell, BellOff, ArrowUpDown, ArrowUp, ArrowDown,
  User, DollarSign, Users, UserCog, LogOut, ShieldCheck, CalendarDays, History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import WorkOrderTable from "@/components/WorkOrderTable";
import WorkOrderForm from "@/components/WorkOrderForm";
import WorkOrderDetails from "@/components/WorkOrderDetails";
import EngineerDirectoryDialog from "@/components/EngineerDirectoryDialog";
import UserManagementDialog from "@/components/UserManagementDialog";
import PermissionMatrixDialog from "@/components/PermissionMatrixDialog";
import PermissionAuditDialog from "@/components/PermissionAuditDialog";
import AbsenceCalendarDialog from "@/components/AbsenceCalendarDialog";
import FinanceDialog from "@/components/FinanceDialog";
import AbsenceWidget from "@/components/AbsenceWidget";
import { APP_VERSION } from "@/version";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { exportToPDF, exportToExcel } from "@/utils/exportUtils";
import { countOverdue, getOverdueStatus } from "@/utils/overdueUtils";
import {
  getNotificationPermission,
  requestNotificationPermission,
  sendOverdueNotifications,
} from "@/utils/notificationUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Priority sort order: P5 (most urgent) → P1 (least urgent)
const PRIORITY_ORDER = { P5: 1, P4: 2, P3: 3, P2: 4, P1: 5 };

const Dashboard = () => {
  const { user: currentUser, logout, can } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [engineerFilter, setEngineerFilter] = useState("all");
  const [chargeableFilter, setChargeableFilter] = useState("all"); // all | chargeable | non_chargeable
  const [quickFilter, setQuickFilter] = useState("all"); // all | overdue | approaching
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDirectoryOpen, setIsDirectoryOpen] = useState(false);
  const [isUserMgmtOpen, setIsUserMgmtOpen] = useState(false);
  const [isPermMatrixOpen, setIsPermMatrixOpen] = useState(false);
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isFinanceOpen, setIsFinanceOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [notifPermission, setNotifPermission] = useState(getNotificationPermission());

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== "all") params.status = statusFilter;
      if (engineerFilter !== "all") params.engineer_name = engineerFilter;
      if (chargeableFilter !== "all") params.is_chargeable = chargeableFilter === "chargeable";

      const response = await axios.get(`${API}/work-orders`, { params });
      setWorkOrders(response.data);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      toast.error("Failed to fetch work orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchEngineers = async () => {
    try {
      const response = await axios.get(`${API}/engineers`);
      setEngineers(response.data);
    } catch (error) {
      console.error("Error fetching engineers:", error);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, [searchTerm, statusFilter, engineerFilter, chargeableFilter]);

  useEffect(() => {
    fetchEngineers();
  }, []);

  // Apply quick filter and sort locally
  const filteredWorkOrders = useMemo(() => {
    let result = [...workOrders];

    // Quick filter
    if (quickFilter === "overdue") {
      result = result.filter((wo) => getOverdueStatus(wo) === "overdue");
    } else if (quickFilter === "approaching") {
      result = result.filter((wo) => getOverdueStatus(wo) === "approaching");
    } else if (quickFilter === "unbilled") {
      // Chargeable work orders with no amount set yet
      result = result.filter(
        (wo) => wo.is_chargeable !== false && (wo.amount == null || Number(wo.amount) <= 0)
      );
    }

    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      if (sortBy === "priority") {
        cmp = (PRIORITY_ORDER[a.priority] || 99) - (PRIORITY_ORDER[b.priority] || 99);
      } else if (sortBy === "end_datetime") {
        cmp = new Date(a.end_datetime) - new Date(b.end_datetime);
      } else if (sortBy === "wo_number") {
        cmp = a.work_order_number.localeCompare(b.work_order_number);
      } else if (sortBy === "status") {
        cmp = (a.status || "").localeCompare(b.status || "");
      } else {
        // created_at default
        cmp = new Date(a.created_at) - new Date(b.created_at);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return result;
  }, [workOrders, quickFilter, sortBy, sortDir]);

  // Send browser notifications for overdue WOs when data changes
  useEffect(() => {
    if (notifPermission === "granted" && workOrders.length > 0) {
      const count = sendOverdueNotifications(workOrders);
      if (count > 0) {
        toast.info(`Sent ${count} overdue notification${count > 1 ? "s" : ""}`);
      }
    }
  }, [workOrders, notifPermission]);

  // Periodically re-check for new overdue WOs (every 60 seconds)
  useEffect(() => {
    if (notifPermission !== "granted") return;
    const interval = setInterval(() => {
      sendOverdueNotifications(workOrders);
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [workOrders, notifPermission]);

  // Poll backend version — if it's newer than this tab's build, nudge a refresh.
  useEffect(() => {
    let notified = false;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/version`);
        const serverVersion = res.data?.version;
        if (serverVersion && serverVersion !== APP_VERSION && !notified) {
          notified = true;
          toast.message("New version available", {
            description: `Server is on v${serverVersion}, you're on v${APP_VERSION}. Click to refresh.`,
            duration: Infinity,
            action: {
              label: "Refresh",
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch (_) {
        // silent — never block UX on this check
      }
    };
    check(); // initial check
    const interval = setInterval(check, 2 * 60 * 1000); // every 2 min
    return () => clearInterval(interval);
  }, []);

  const handleEnableNotifications = async () => {
    const result = await requestNotificationPermission();
    setNotifPermission(result);
    if (result === "granted") {
      toast.success("Notifications enabled");
      sendOverdueNotifications(workOrders);
    } else if (result === "denied") {
      toast.error("Notifications blocked. Please enable them in your browser settings.");
    } else if (result === "unsupported") {
      toast.error("Notifications not supported in this browser");
    }
  };

  const handleCreateWorkOrder = () => {
    setSelectedWorkOrder(null);
    setIsEditing(false);
    setIsFormOpen(true);
  };

  const handleEditWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setIsEditing(true);
    setIsFormOpen(true);
  };

  const handleViewWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setIsDetailsOpen(true);
  };

  const handleRefreshWorkOrder = async () => {
    await fetchWorkOrders();
    if (selectedWorkOrder) {
      const updated = workOrders.find((wo) => wo.id === selectedWorkOrder.id);
      if (updated) {
        setSelectedWorkOrder(updated);
      }
    }
  };

  const handleDeleteWorkOrder = async (id) => {
    if (!window.confirm("Are you sure you want to delete this work order?")) return;
    try {
      await axios.delete(`${API}/work-orders/${id}`);
      toast.success("Work order deleted successfully");
      fetchWorkOrders();
    } catch (error) {
      console.error("Error deleting work order:", error);
      toast.error("Failed to delete work order");
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir(field === "priority" ? "asc" : "desc");
    }
  };

  const handleExportPDF = () => {
    exportToPDF(filteredWorkOrders);
    toast.success("Exported to PDF");
  };

  const handleExportExcel = () => {
    exportToExcel(filteredWorkOrders);
    toast.success("Exported to Excel");
  };

  const { overdue: overdueCount, approaching: approachingCount } = countOverdue(workOrders);
  const unbilledCount = useMemo(
    () =>
      workOrders.filter(
        (wo) => wo.is_chargeable !== false && (wo.amount == null || Number(wo.amount) <= 0)
      ).length,
    [workOrders]
  );
  const canSeeBilling = currentUser?.role === "finance" || currentUser?.role === "manager" || currentUser?.role === "admin";

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="dashboard-container">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-[1600px] mx-auto px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#09090B]" style={{ fontFamily: "Manrope" }}>
                Work Order Management
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <p className="text-sm text-[#71717A]">Track and manage all work orders</p>
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase bg-[#F4F4F5] text-[#52525B] border border-[#E4E4E7]"
                  data-testid="app-version-badge"
                  title={`App version ${APP_VERSION}`}
                >
                  v{APP_VERSION}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => setIsDirectoryOpen(true)}
                variant="outline"
                className="border-[#E4E4E7] rounded-sm"
                data-testid="open-directory-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Engineers
              </Button>
              {notifPermission !== "granted" && notifPermission !== "unsupported" && (
                <Button
                  onClick={handleEnableNotifications}
                  variant="outline"
                  className="border-[#E4E4E7] rounded-sm"
                  data-testid="enable-notifications-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Alerts
                </Button>
              )}
              {notifPermission === "granted" && (
                <div
                  className="flex items-center gap-2 text-xs text-[#059669] font-semibold px-3 py-2 border border-[#A7F3D0] bg-[#D1FAE5] rounded-sm"
                  data-testid="notifications-enabled-indicator"
                >
                  <Bell className="w-4 h-4" />
                  Alerts On
                </div>
              )}
              <Button
                onClick={handleCreateWorkOrder}
                disabled={!can("work_orders.edit")}
                title={!can("work_orders.edit") ? "You don't have permission to create work orders" : undefined}
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-sm px-6 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="create-work-order-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Work Order
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-10 h-10 rounded-sm bg-[#0F172A] text-white flex items-center justify-center text-sm font-bold hover:bg-[#1E293B] transition-colors"
                    data-testid="user-menu-btn"
                    title={currentUser?.name}
                  >
                    {(currentUser?.name || "?").charAt(0).toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold text-[#09090B]" data-testid="user-menu-name">
                      {currentUser?.name}
                    </div>
                    <div className="text-xs text-[#71717A] font-normal truncate" data-testid="user-menu-email">
                      @{currentUser?.username || "—"} • {currentUser?.role || "admin"}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {currentUser?.role === "manager" && (
                    <DropdownMenuItem onClick={() => setIsUserMgmtOpen(true)} data-testid="open-user-mgmt-btn">
                      <UserCog className="w-4 h-4 mr-2" />
                      Manage Users
                    </DropdownMenuItem>
                  )}
                  {currentUser?.role === "manager" && (
                    <DropdownMenuItem
                      onClick={() => setIsPermMatrixOpen(true)}
                      data-testid="open-perm-matrix-btn"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Role Permissions
                    </DropdownMenuItem>
                  )}
                  {currentUser?.role === "manager" && (
                    <DropdownMenuItem
                      onClick={() => setIsAuditOpen(true)}
                      data-testid="open-audit-btn"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Permission Log
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => setIsCalendarOpen(true)}
                    data-testid="open-calendar-btn"
                  >
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Absence Calendar
                  </DropdownMenuItem>
                  {(currentUser?.role === "finance" || currentUser?.role === "manager") && (
                    <DropdownMenuItem
                      onClick={() => setIsFinanceOpen(true)}
                      data-testid="open-finance-btn"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Finance
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      logout();
                      toast.success("Signed out");
                    }}
                    className="text-[#DC2626] focus:text-[#DC2626] focus:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Absence Widget — next 7 days coverage gaps */}
        <AbsenceWidget onOpenCalendar={() => setIsCalendarOpen(true)} />

        {/* Alert Summary */}
        {(overdueCount > 0 || approachingCount > 0 || (canSeeBilling && unbilledCount > 0)) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {overdueCount > 0 && (
              <button
                type="button"
                onClick={() => setQuickFilter(quickFilter === "overdue" ? "all" : "overdue")}
                className={`flex items-center gap-3 rounded-sm p-4 text-left transition-all duration-200 ${
                  quickFilter === "overdue"
                    ? "bg-[#DC2626] border-2 border-[#DC2626] text-white"
                    : "bg-[#FEE2E2] border border-[#FECACA] hover:bg-[#FECACA]"
                }`}
                data-testid="overdue-alert-banner"
              >
                <div
                  className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                    quickFilter === "overdue" ? "bg-white" : "bg-[#DC2626]"
                  }`}
                >
                  <AlertTriangle className={`w-5 h-5 ${quickFilter === "overdue" ? "text-[#DC2626]" : "text-white"}`} />
                </div>
                <div className="flex-1">
                  <div
                    className={`text-xs tracking-[0.2em] uppercase font-bold ${
                      quickFilter === "overdue" ? "text-white" : "text-[#DC2626]"
                    }`}
                  >
                    Overdue {quickFilter === "overdue" && "(Filtering)"}
                  </div>
                  <div className={`text-lg font-semibold ${quickFilter === "overdue" ? "text-white" : "text-[#09090B]"}`}>
                    <span data-testid="overdue-count">{overdueCount}</span> work order{overdueCount > 1 ? "s" : ""} past due date
                  </div>
                </div>
              </button>
            )}
            {approachingCount > 0 && (
              <button
                type="button"
                onClick={() => setQuickFilter(quickFilter === "approaching" ? "all" : "approaching")}
                className={`flex items-center gap-3 rounded-sm p-4 text-left transition-all duration-200 ${
                  quickFilter === "approaching"
                    ? "bg-[#EA580C] border-2 border-[#EA580C] text-white"
                    : "bg-[#FFEDD5] border border-[#FED7AA] hover:bg-[#FED7AA]"
                }`}
                data-testid="approaching-alert-banner"
              >
                <div
                  className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                    quickFilter === "approaching" ? "bg-white" : "bg-[#EA580C]"
                  }`}
                >
                  <Clock className={`w-5 h-5 ${quickFilter === "approaching" ? "text-[#EA580C]" : "text-white"}`} />
                </div>
                <div className="flex-1">
                  <div
                    className={`text-xs tracking-[0.2em] uppercase font-bold ${
                      quickFilter === "approaching" ? "text-white" : "text-[#EA580C]"
                    }`}
                  >
                    Due Soon {quickFilter === "approaching" && "(Filtering)"}
                  </div>
                  <div className={`text-lg font-semibold ${quickFilter === "approaching" ? "text-white" : "text-[#09090B]"}`}>
                    <span data-testid="approaching-count">{approachingCount}</span> work order{approachingCount > 1 ? "s" : ""} due within 24 hours
                  </div>
                </div>
              </button>
            )}
            {canSeeBilling && unbilledCount > 0 && (
              <button
                type="button"
                onClick={() => setQuickFilter(quickFilter === "unbilled" ? "all" : "unbilled")}
                className={`flex items-center gap-3 rounded-sm p-4 text-left transition-all duration-200 ${
                  quickFilter === "unbilled"
                    ? "bg-[#D97706] border-2 border-[#D97706] text-white"
                    : "bg-[#FEF3C7] border border-[#FDE68A] hover:bg-[#FDE68A]"
                }`}
                data-testid="unbilled-alert-banner"
              >
                <div
                  className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                    quickFilter === "unbilled" ? "bg-white" : "bg-[#D97706]"
                  }`}
                >
                  <DollarSign className={`w-5 h-5 ${quickFilter === "unbilled" ? "text-[#D97706]" : "text-white"}`} />
                </div>
                <div className="flex-1">
                  <div
                    className={`text-xs tracking-[0.2em] uppercase font-bold ${
                      quickFilter === "unbilled" ? "text-white" : "text-[#B45309]"
                    }`}
                  >
                    Not Billed {quickFilter === "unbilled" && "(Filtering)"}
                  </div>
                  <div className={`text-lg font-semibold ${quickFilter === "unbilled" ? "text-white" : "text-[#09090B]"}`}>
                    <span data-testid="unbilled-count">{unbilledCount}</span> chargeable work order{unbilledCount > 1 ? "s" : ""} awaiting amount
                  </div>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#71717A] w-4 h-4" />
              <Input
                placeholder="Search by WO number, description, site, or engineer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#E4E4E7] rounded-sm"
                data-testid="search-input"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Quick Filter */}
              <Select value={quickFilter} onValueChange={setQuickFilter}>
                <SelectTrigger className="w-[160px] border-[#E4E4E7] rounded-sm" data-testid="quick-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="quick-filter-all">All Orders</SelectItem>
                  <SelectItem value="overdue" data-testid="quick-filter-overdue">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-[#DC2626]" />
                      Overdue Only
                    </span>
                  </SelectItem>
                  <SelectItem value="approaching" data-testid="quick-filter-approaching">
                    <span className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-[#EA580C]" />
                      Due Soon Only
                    </span>
                  </SelectItem>
                  {canSeeBilling && (
                    <SelectItem value="unbilled" data-testid="quick-filter-unbilled">
                      <span className="flex items-center gap-2">
                        <DollarSign className="w-3.5 h-3.5 text-[#D97706]" />
                        Not Billed Only
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[170px] border-[#E4E4E7] rounded-sm" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Assigned">Assigned</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              {/* Engineer Filter (My Work Orders) */}
              <Select value={engineerFilter} onValueChange={setEngineerFilter}>
                <SelectTrigger className="w-[190px] border-[#E4E4E7] rounded-sm" data-testid="engineer-filter">
                  <User className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by engineer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="engineer-filter-all">All Engineers</SelectItem>
                  {engineers.map((eng) => (
                    <SelectItem key={eng} value={eng} data-testid={`engineer-filter-${eng.replace(/\s+/g, '-').toLowerCase()}`}>
                      {eng}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Chargeable Filter */}
              <Select value={chargeableFilter} onValueChange={setChargeableFilter}>
                <SelectTrigger className="w-[170px] border-[#E4E4E7] rounded-sm" data-testid="chargeable-filter">
                  <DollarSign className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Billing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="chargeable-filter-all">All Billing</SelectItem>
                  <SelectItem value="chargeable" data-testid="chargeable-filter-chargeable">Chargeable</SelectItem>
                  <SelectItem value="non_chargeable" data-testid="chargeable-filter-non">Non-Chargeable</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select
                value={`${sortBy}:${sortDir}`}
                onValueChange={(val) => {
                  const [field, dir] = val.split(":");
                  setSortBy(field);
                  setSortDir(dir);
                }}
              >
                <SelectTrigger className="w-[200px] border-[#E4E4E7] rounded-sm" data-testid="sort-select">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at:desc" data-testid="sort-newest">Newest First</SelectItem>
                  <SelectItem value="created_at:asc" data-testid="sort-oldest">Oldest First</SelectItem>
                  <SelectItem value="priority:asc" data-testid="sort-priority-urgent">Priority: Urgent First</SelectItem>
                  <SelectItem value="priority:desc" data-testid="sort-priority-low">Priority: Low First</SelectItem>
                  <SelectItem value="end_datetime:asc" data-testid="sort-end-earliest">End Date: Earliest</SelectItem>
                  <SelectItem value="end_datetime:desc" data-testid="sort-end-latest">End Date: Latest</SelectItem>
                  <SelectItem value="wo_number:asc" data-testid="sort-wo-number">WO Number</SelectItem>
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-[#E4E4E7] rounded-sm"
                    data-testid="export-dropdown"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Active filter indicators */}
          {(quickFilter !== "all" || sortBy !== "created_at" || statusFilter !== "all" || engineerFilter !== "all" || chargeableFilter !== "all") && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#E4E4E7]">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">Active:</span>
              {quickFilter !== "all" && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#F4F4F5] border border-[#E4E4E7]"
                  data-testid="active-filter-quick"
                >
                  {quickFilter === "overdue" && "Overdue Only"}
                  {quickFilter === "approaching" && "Due Soon Only"}
                  {quickFilter === "unbilled" && "Not Billed Only"}
                  <button
                    onClick={() => setQuickFilter("all")}
                    className="text-[#71717A] hover:text-[#09090B]"
                    aria-label="Clear quick filter"
                  >
                    ×
                  </button>
                </span>
              )}
              {engineerFilter !== "all" && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#F4F4F5] border border-[#E4E4E7]"
                  data-testid="active-filter-engineer"
                >
                  <User className="w-3 h-3" />
                  {engineerFilter}
                  <button
                    onClick={() => setEngineerFilter("all")}
                    className="text-[#71717A] hover:text-[#09090B]"
                    aria-label="Clear engineer filter"
                  >
                    ×
                  </button>
                </span>
              )}
              {chargeableFilter !== "all" && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#F4F4F5] border border-[#E4E4E7]"
                  data-testid="active-filter-chargeable"
                >
                  <DollarSign className="w-3 h-3" />
                  {chargeableFilter === "chargeable" ? "Chargeable" : "Non-Chargeable"}
                  <button
                    onClick={() => setChargeableFilter("all")}
                    className="text-[#71717A] hover:text-[#09090B]"
                    aria-label="Clear chargeable filter"
                  >
                    ×
                  </button>
                </span>
              )}
              {statusFilter !== "all" && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#F4F4F5] border border-[#E4E4E7]"
                  data-testid="active-filter-status"
                >
                  Status: {statusFilter}
                  <button
                    onClick={() => setStatusFilter("all")}
                    className="text-[#71717A] hover:text-[#09090B]"
                    aria-label="Clear status filter"
                  >
                    ×
                  </button>
                </span>
              )}
              {sortBy !== "created_at" && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold bg-[#F4F4F5] border border-[#E4E4E7]"
                  data-testid="active-filter-sort"
                >
                  Sort: {sortBy.replace("_", " ")} {sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Work Orders Table */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm">
          <WorkOrderTable
            workOrders={filteredWorkOrders}
            loading={loading}
            onView={handleViewWorkOrder}
            onEdit={handleEditWorkOrder}
            onDelete={handleDeleteWorkOrder}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
        </div>

        {/* Result summary */}
        {!loading && (
          <div className="mt-4 text-xs text-[#71717A]" data-testid="result-summary">
            Showing {filteredWorkOrders.length} of {workOrders.length} work orders
          </div>
        )}
      </main>

      {/* Dialogs */}
      <WorkOrderForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        workOrder={selectedWorkOrder}
        isEditing={isEditing}
        onSuccess={() => {
          fetchWorkOrders();
          fetchEngineers();
        }}
        existingEngineers={engineers}
      />

      <WorkOrderDetails
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        workOrder={selectedWorkOrder}
        onEdit={handleEditWorkOrder}
        onRefresh={handleRefreshWorkOrder}
      />

      <EngineerDirectoryDialog
        open={isDirectoryOpen}
        onOpenChange={setIsDirectoryOpen}
        onDirectoryChange={fetchEngineers}
      />

      <UserManagementDialog
        open={isUserMgmtOpen}
        onOpenChange={setIsUserMgmtOpen}
      />

      <PermissionMatrixDialog
        open={isPermMatrixOpen}
        onOpenChange={setIsPermMatrixOpen}
      />

      <PermissionAuditDialog
        open={isAuditOpen}
        onOpenChange={setIsAuditOpen}
      />

      <AbsenceCalendarDialog
        open={isCalendarOpen}
        onOpenChange={setIsCalendarOpen}
      />

      <FinanceDialog
        open={isFinanceOpen}
        onOpenChange={setIsFinanceOpen}
      />
    </div>
  );
};

export default Dashboard;
